import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function InsightsTab() {
  const [insights, setInsights] = useState([]);
  const [outcomes, setOutcomes] = useState(null);
  const [negReport, setNegReport] = useState(null);
  const [selectedPayer, setSelectedPayer] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [iR, oR] = await Promise.all([
          api.get('/rcm/automation/contract-insights').catch(() => ({ data: { insights: [] } })),
          api.get('/rcm/automation/outcomes').catch(() => ({ data: { outcomes: [], summary: {} } })),
        ]);
        setInsights(iR.data.insights || []);
        setOutcomes(oR.data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const loadNegReport = async (payerId) => {
    setSelectedPayer(payerId);
    try { const r = await api.get(`/rcm/automation/negotiation-report/${payerId}`); setNegReport(r.data); }
    catch { setNegReport(null); }
  };

  if (loading) return <div className="p-4 text-xs text-muted-foreground">Loading insights...</div>;

  return (
    <div className="space-y-4" data-testid="insights-tab">
      {/* Outcome summary */}
      {outcomes && Object.keys(outcomes.summary || {}).length > 0 && (
        <Card>
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Outcome Summary</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="flex flex-wrap gap-3">
              {Object.entries(outcomes.summary).map(([state, data]) => (
                <div key={state} className="text-center">
                  <p className="text-lg font-bold">{data.count}</p>
                  <p className="text-[10px] text-muted-foreground">{state.replace(/_/g, ' ')}</p>
                  {data.recovered > 0 && <p className="text-[10px] text-emerald-600">{fmt(data.recovered)}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Insights */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs flex items-center gap-2">Payment Pattern Insights <PredictiveLabel /></CardTitle></CardHeader>
        <CardContent className="px-3 pb-3">
          {insights.length === 0 ? <p className="text-xs text-muted-foreground py-3 text-center">No contract models available. Build models first.</p> : (
          <table className="w-full text-xs"><thead><tr className="border-b text-left">
            <th className="py-1 font-medium text-muted-foreground">Payer</th>
            <th className="py-1 font-medium text-muted-foreground">CPT</th>
            <th className="py-1 font-medium text-muted-foreground">Pattern</th>
            <th className="py-1 font-medium text-muted-foreground text-right">Expected</th>
            <th className="py-1 font-medium text-muted-foreground text-right">Observed</th>
            <th className="py-1 font-medium text-muted-foreground text-right">Dev</th>
            <th className="py-1 font-medium text-muted-foreground">Confidence</th>
            <th className="py-1"></th>
          </tr></thead><tbody>{insights.slice(0, 15).map((ins, i) => (
            <tr key={i} className="border-b hover:bg-muted/30">
              <td className="py-1">{ins.payer_name}</td>
              <td className="py-1 font-mono">{ins.cpt_code}</td>
              <td className="py-1"><StatusChip status={ins.pattern} size="xs" /></td>
              <td className="py-1 text-right">{fmt(ins.expected_range?.low)}–{fmt(ins.expected_range?.high)}</td>
              <td className="py-1 text-right font-medium">{fmt(ins.observed_avg)}</td>
              <td className={`py-1 text-right ${ins.deviation_pct > 0.05 ? 'text-red-600' : 'text-emerald-600'}`}>{(ins.deviation_pct * 100).toFixed(1)}%</td>
              <td className="py-1"><ConfidenceText level={ins.confidence} /></td>
              <td className="py-1">
                <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1" onClick={() => loadNegReport(ins.payer_id)}>
                  <ChevronRight className="h-2.5 w-2.5" />
                </Button>
              </td>
            </tr>
          ))}</tbody></table>)}
          {insights.some(i => i.sample_size_warning) && (
            <p className="text-[10px] text-amber-600 mt-2">Some insights have low sample sizes — treat as directional guidance.</p>
          )}
        </CardContent>
      </Card>

      {/* Negotiation Report Slide */}
      <SlidePanel open={!!negReport} onClose={() => { setNegReport(null); setSelectedPayer(''); }} title="Negotiation Prep">
        {negReport && (
          <>
            <Card><CardContent className="p-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Payer</span><span className="font-medium">{negReport.payer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Confidence</span><ConfidenceText level={negReport.report_confidence} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sample</span><span>{negReport.sample_size} claims</span></div>
              <div className="border-t pt-1.5 mt-1.5" />
              <div className="flex justify-between"><span>Charges</span><span className="font-medium">{fmt(negReport.payment_summary?.total_charges)}</span></div>
              <div className="flex justify-between"><span>Paid</span><span className="text-emerald-600 font-medium">{fmt(negReport.payment_summary?.total_paid)}</span></div>
              <div className="flex justify-between"><span>Ratio</span><span>{((negReport.payment_summary?.charge_to_pay_ratio || 0) * 100).toFixed(0)}%</span></div>
              <div className="flex justify-between"><span>Denial Rate</span><span className={negReport.denial_summary?.denial_rate > 0.1 ? 'text-red-600' : ''}>{((negReport.denial_summary?.denial_rate || 0) * 100).toFixed(1)}%</span></div>
              <div className="flex justify-between"><span>Avg Days</span><span>{negReport.timing?.avg_days_to_payment?.toFixed(0)}d</span></div>
            </CardContent></Card>

            <Card className="border-primary/20">
              <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Talking Points</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3 space-y-1.5">
                {negReport.talking_points?.map((tp, i) => (
                  <div key={i} className="flex gap-2 text-xs"><span className="text-primary font-bold shrink-0">{i + 1}.</span><span>{tp}</span></div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-slate-50/30">
              <CardContent className="p-2.5 text-[10px] text-muted-foreground">
                {negReport.methodology}
              </CardContent>
            </Card>
          </>
        )}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// INTELLIGENCE TAB (Phase 6.6) — READ-ONLY
// ═══════════════════════════════════════

export { InsightsTab };
